import { ChevronDownIcon } from '@chakra-ui/icons'
import { Box, Button, MenuButton, Text } from '@chakra-ui/react'
import { Token, Width } from 'types/chakra'
import { IAsyncSelectOption } from 'interfaces/common'
import {
  AUTH_MOBILE_INPUT_BORDER,
  DARK_GREY,
  DARK_TEXT_MUTED,
  DARK_TEXT_PRIMARY
} from 'constants/dark-tokens'

interface IDropdownButtonProps {
  items: IAsyncSelectOption<number>[]
  placeHolder?: string
  isDisabled?: boolean
  width?: Token<Width | number, 'sizes'>
  height?: Token<Width | number, 'sizes'>
  /** When true applies dark surface palette — leave false (default) for light contexts */
  dark?: boolean
}

/**
 * Static trigger: always shows the first selected label (+N) or the placeholder,
 * with the chevron pinned to the right edge. It never swaps itself for an input,
 * so hovering or opening the menu leaves the selection and layout untouched.
 */
const DropdownButton = (props: IDropdownButtonProps) => {
  const { items, placeHolder, width, height, isDisabled, dark = false } = props
  const hasSelection = items?.length > 0
  const label = hasSelection ? items[0]?.label ?? '' : placeHolder ?? ''
  const extraCount = items?.length > 1 ? `+${items.length - 1}` : ''

  const textColor = dark
    ? hasSelection ? DARK_TEXT_PRIMARY : DARK_TEXT_MUTED
    : hasSelection ? 'gray.700' : 'blackAlpha.500'
  const mutedColor = dark ? DARK_TEXT_MUTED : 'gray.500'

  // No accent ring while open — the trigger keeps its neutral border; only hover lifts it.
  const borderColor = dark ? AUTH_MOBILE_INPUT_BORDER : '#E2E8F0'
  const hoverStyle = dark ? { background: 'rgba(255,255,255,0.04)', borderColor: DARK_GREY } : { background: 'gray.200' }

  return (
    <MenuButton
      as={Button}
      type="button"
      isDisabled={isDisabled}
      width={width}
      height={height ?? 8}
      // Without this the filter row squeezes the trigger below its declared width.
      flexShrink={0}
      paddingX={3}
      fontSize={{ base: 'md', lg: 'sm' }}
      fontWeight="normal"
      textAlign="left"
      color={textColor}
      background={dark ? 'transparent' : 'white'}
      border="1px solid"
      borderColor={borderColor}
      boxShadow="none"
      _hover={hoverStyle}
      // While open the button is `isActive`; without an override Chakra's solid
      // `_active` paints it light grey on the dark surface.
      _active={dark ? { background: 'rgba(255,255,255,0.04)' } : undefined}
      _focus={{ outline: 'none', boxShadow: 'none' }}
      _focusVisible={dark ? { borderColor: DARK_GREY, boxShadow: 'none' } : { boxShadow: 'outline' }}
      // MenuButton wraps children in a span; make it a full-width row so the
      // chevron sits at the right edge whatever the label length.
      sx={{ '& > span': { display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 } }}
    >
      <Text
        as="span"
        flex={1}
        minWidth={0}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        color="inherit"
      >
        {label}
      </Text>
      {extraCount && (
        <Text as="span" flexShrink={0} marginLeft={1} color={mutedColor}>
          {extraCount}
        </Text>
      )}
      <Box as="span" display="inline-flex" flexShrink={0} marginLeft={2}>
        <ChevronDownIcon boxSize={5} color={dark ? DARK_TEXT_MUTED : 'gray.700'} />
      </Box>
    </MenuButton>
  )
}

export default DropdownButton
